import React, { useCallback, useRef, useState } from 'react';
import ReactDOM from 'react-dom';

import App from 'App';

import { truncateFileName } from 'Helpers/misc';

import LoadingSpinner from 'CommonComponents/LoadingSpinner';

import useApiRequest from 'Hooks/useApiRequest';

import CircleCheckIcon from 'Assets/icons/circle-check.svg';
import CloudArrowUpIcon from 'Assets/icons/cloud-arrow-up.svg';
import DownloadIcon from 'Assets/icons/download.svg';
import FileTextIcon from 'Assets/icons/file-text.svg';
import TrashIcon from 'Assets/icons/trash.svg';

const SIGN_AND_ATTACH = 'sign_and_attach';
const COMPLETE_AND_ATTACH = 'complete_and_attach';
const ATTACH = 'attach';

const ACCEPTED_TYPES = {
    [SIGN_AND_ATTACH]: '.pdf',
    [COMPLETE_AND_ATTACH]: '.pdf,.docx,.xlsx',
};

const TYPE_LABELS = {
    [SIGN_AND_ATTACH]: 'Accepted format: PDF',
    [COMPLETE_AND_ATTACH]: 'Accepted formats: PDF, Word, Excel',
    [ATTACH]: 'All file types accepted',
};

const UPLOAD_LABELS = {
    [SIGN_AND_ATTACH]: 'Upload signed document',
    [COMPLETE_AND_ATTACH]: 'Upload completed document',
    [ATTACH]: 'Upload your documents',
};

function DropZone({ accept, onFiles, children }) {
    const [dragging, setDragging] = useState(false);
    const counter = useRef(0);

    const handleDragEnter = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        counter.current++;
        setDragging(true);
    }, []);

    const handleDragLeave = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        counter.current--;
        if (counter.current === 0) setDragging(false);
    }, []);

    const handleDrop = useCallback(
        (e) => {
            e.preventDefault();
            e.stopPropagation();
            counter.current = 0;
            setDragging(false);
            const dropped = Array.from(e.dataTransfer.files);
            if (dropped.length > 0) onFiles(dropped);
        },
        [onFiles],
    );

    return (
        <div
            onDragEnter={handleDragEnter}
            onDragOver={(e) => e.preventDefault()}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            className={`tw-border-2 tw-border-dashed tw-rounded-lg tw-p-6 tw-text-center tw-transition-colors tw-cursor-pointer ${
                dragging ? 'tw-border-blue-400 tw-bg-blue-400/10' : 'tw-border-gray-500 hover:tw-border-gray-400'
            }`}
        >
            {children}
        </div>
    );
}

export default function ExternalSupplierPortal({ field, submitUrl, downloadUrls }) {
    const fileInputRef = useRef(null);
    const [files, setFiles] = useState([]);
    const [submitted, setSubmitted] = useState(false);
    const [errors, setErrors] = useState({});

    const request = useApiRequest({
        url: submitUrl,
        options: { headers: { 'Content-Type': 'multipart/form-data' } },
        displayErrorToast: false,
    });

    const addFiles = useCallback((newFiles) => {
        setFiles((prev) => [...prev, ...newFiles]);
        setErrors({});
    }, []);

    const handleFileChange = (event) => {
        const selected = Array.from(event.target.files);
        addFiles(selected);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const removeFile = (index) => {
        setFiles((prev) => prev.filter((_, i) => i !== index));
    };

    const handleSubmit = (event) => {
        event.preventDefault();

        if (files.length === 0) {
            setErrors({ files: $t('At least one file is required') });
            return;
        }

        const formData = new FormData();
        files.forEach((file) => {
            formData.append('files[]', file);
        });

        request
            .post(formData)
            .then(() => {
                setSubmitted(true);
            })
            .catch((err) => {
                if (err.response?.data?.errors) {
                    setErrors(err.response.data.errors);
                } else {
                    setErrors({ files: err.response?.data?.message || $t('An error occurred, please try again') });
                }
            });
    };

    if (submitted) {
        return (
            <div className="inbud-card tw-w-full">
                <div className="tw-flex tw-flex-col tw-items-center tw-gap-4 tw-py-8">
                    <div className="tw-w-14 tw-h-14 tw-rounded-full tw-bg-green/10 tw-flex tw-items-center tw-justify-center">
                        <CircleCheckIcon className="tw-w-8 tw-h-8 tw-text-green" />
                    </div>
                    <p className="tw-text-lg tw-font-medium tw-text-white">{$t('Your documents have been submitted successfully.')}</p>
                    <p className="tw-text-sm tw-text-gray-400">{$t('You may close this page.')}</p>
                </div>
            </div>
        );
    }

    const hasDownloads = field.type !== ATTACH && field.attachments && field.attachments.length > 0;
    const firstError = errors.files || errors['files.0'] || Object.values(errors)[0];

    return (
        <div className="inbud-card tw-w-full">
            <form onSubmit={handleSubmit}>
                <div className="tw-flex tw-flex-col tw-gap-6">
                    {field.instruction && (
                        <div>
                            <h3 className="tw-text-sm tw-font-semibold tw-mb-1">{$t('Instructions')}</h3>
                            <p className="tw-text-sm tw-text-gray-300">{field.instruction}</p>
                        </div>
                    )}

                    {hasDownloads && (
                        <div>
                            <h3 className="tw-text-sm tw-font-semibold tw-mb-3">{$t('Documents to download')}</h3>
                            <div className="tw-space-y-2">
                                {field.attachments.map((attachment) => (
                                    <a
                                        key={attachment.id}
                                        href={downloadUrls[attachment.id]}
                                        className="tw-flex tw-items-center tw-gap-3 tw-p-3 tw-rounded-lg tw-border tw-border-gray-600 hover:tw-border-blue-400 tw-transition-colors tw-group"
                                    >
                                        <DownloadIcon className="tw-w-5 tw-h-5 tw-text-gray-400 tw-flex-shrink-0 group-hover:tw-text-blue-400" />
                                        <span className="tw-text-sm tw-text-gray-300 group-hover:tw-text-blue-400 tw-truncate">{attachment.name}</span>
                                    </a>
                                ))}
                            </div>
                        </div>
                    )}

                    <div>
                        <h3 className="tw-text-sm tw-font-semibold tw-mb-3">{$t(UPLOAD_LABELS[field.type] || UPLOAD_LABELS[ATTACH])}</h3>

                        <DropZone accept={ACCEPTED_TYPES[field.type]} onFiles={addFiles}>
                            <input
                                ref={fileInputRef}
                                type="file"
                                multiple
                                accept={ACCEPTED_TYPES[field.type] || undefined}
                                onChange={handleFileChange}
                                className="tw-hidden"
                                id="file-upload"
                            />
                            <label htmlFor="file-upload" className="tw-cursor-pointer">
                                <div className="tw-flex tw-flex-col tw-items-center tw-gap-2">
                                    <CloudArrowUpIcon className="tw-w-8 tw-h-8 tw-text-gray-400" />
                                    <span className="tw-text-sm tw-text-gray-300">
                                        {$t('Drag and drop files here, or')} <span className="tw-text-blue-400 tw-font-medium">{$t('browse')}</span>
                                    </span>
                                    <span className="tw-text-xs tw-text-gray-500">{$t(TYPE_LABELS[field.type] || TYPE_LABELS[ATTACH])}</span>
                                </div>
                            </label>
                        </DropZone>

                        {files.length > 0 && (
                            <div className="tw-mt-3 tw-space-y-2">
                                {files.map((file, index) => (
                                    <div key={index} className="tw-flex tw-items-center tw-justify-between tw-p-2 tw-rounded-lg tw-bg-charcoal-light">
                                        <div className="tw-flex tw-items-center tw-gap-2 tw-min-w-0">
                                            <FileTextIcon className="tw-w-4 tw-h-4 tw-text-gray-400 tw-flex-shrink-0" />
                                            <span className="tw-text-sm tw-text-gray-300 tw-truncate">{truncateFileName(file.name)}</span>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => removeFile(index)}
                                            className="tw-flex-shrink-0 tw-ml-2 tw-p-1 tw-rounded hover:tw-bg-red/10 tw-transition-colors"
                                        >
                                            <TrashIcon className="tw-w-3.5 tw-h-3.5 tw-text-red" />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}

                        {firstError && (
                            <div className="tw-mt-2 tw-text-sm tw-text-red">
                                <strong>{Array.isArray(firstError) ? firstError[0] : firstError}</strong>
                            </div>
                        )}
                    </div>

                    <div className="tw-flex tw-justify-end tw-pt-2">
                        <button type="submit" className="button primary" disabled={request.loading || files.length === 0}>
                            {request.loading ? <LoadingSpinner size="sm" ySpacing={false} /> : $t('Submit')}
                        </button>
                    </div>
                </div>
            </form>
        </div>
    );
}

if (document.getElementById('externalSupplierPortal')) {
    var rootEl = document.getElementById('externalSupplierPortal');
    ReactDOM.render(
        <App>
            <ExternalSupplierPortal
                field={JSON.parse(rootEl.getAttribute('data-field'))}
                submitUrl={rootEl.getAttribute('data-submit-url')}
                downloadUrls={JSON.parse(rootEl.getAttribute('data-download-urls'))}
            />
        </App>,
        rootEl,
    );
}
